import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
export function getButtonClasses(props) {
    return cva(classNames('select-none [-webkit-user-drag:none] outline-hidden font-medium transition-[background-color,border-color,color,opacity] duration-300 ease-out font-sans text-center items-center justify-center leading-loose whitespace-nowrap', 
    // Disabled visuals via native :disabled pseudo — applies when the `disabled`
    // attribute is set or when an ancestor <fieldset> is disabled. This handles
    // <button> targets that don't receive a cva `disabled` prop (e.g. children
    // of a disabled fieldset). Wins over the `disabled: false` cva branch via
    // pseudo-class specificity.
    'disabled:cursor-default disabled:opacity-50', getButtonOpenStateClassNames({
        variant: props.variant,
        intent: props.intent,
        inline: props.inline,
    })), {
        variants: {
            variant: {
                primary: 'text-inverse border border-transparent',
                secondary: 'border border-solid',
                tonal: 'border border-transparent',
                text: '',
            },
            intent: {
                neutral: '',
                warning: '',
            },
            size: {
                large: 'h-14 text-lg',
                default: 'h-10 text-base',
                small: 'h-8 text-sm',
                tiny: 'h-6 text-xs',
            },
            disabled: {
                false: 'cursor-pointer',
                true: 'cursor-default opacity-50',
            },
            isLoading: {
                false: '',
                true: 'cursor-wait',
            },
            fullWidth: {
                true: 'flex w-full',
                false: 'inline-flex',
            },
            rounded: {
                true: 'rounded-full aspect-square p-0',
                false: '',
            },
            pill: {
                true: 'rounded-full',
                false: '',
            },
            inline: {
                true: 'px-0',
                false: '',
            },
            iconOnly: {
                true: 'aspect-[9/8]',
                false: '',
            },
        },
        compoundVariants: [
            // Color
            {
                variant: 'primary',
                intent: 'neutral',
                class: 'bg-button-bg',
            },
            {
                variant: 'primary',
                intent: 'warning',
                class: 'bg-negative-strong',
            },
            {
                variant: 'secondary',
                intent: 'neutral',
                class: 'text-primary border-base',
            },
            {
                variant: 'secondary',
                intent: 'warning',
                class: 'text-negative-primary border-negative-strong',
            },
            {
                variant: 'tonal',
                intent: 'neutral',
                class: 'bg-soft text-primary',
            },
            {
                variant: 'tonal',
                intent: 'warning',
                class: 'bg-negative-subtle text-negative-primary',
            },
            {
                variant: 'text',
                intent: 'neutral',
                class: 'text-secondary',
            },
            {
                variant: 'text',
                intent: 'warning',
                class: 'text-negative-primary',
            },
            // Hover Foreground
            {
                variant: 'text',
                intent: 'neutral',
                disabled: false,
                isLoading: false,
                class: 'hover:text-primary',
            },
            // Hover Background
            {
                variant: 'text',
                disabled: false,
                isLoading: false,
                inline: false,
                class: 'hover:bg-soft',
            },
            {
                variant: 'tonal',
                intent: 'neutral',
                disabled: false,
                isLoading: false,
                class: 'hover:bg-subtle',
            },
            {
                variant: 'tonal',
                intent: 'warning',
                disabled: false,
                isLoading: false,
                class: 'hover:bg-negative-soft',
            },
            // Hover Opacity
            {
                variant: 'primary',
                disabled: false,
                isLoading: false,
                class: 'hover:opacity-80',
            },
            // Hover Border
            {
                variant: 'secondary',
                intent: 'neutral',
                disabled: false,
                isLoading: false,
                class: 'hover:border-base hover:bg-subtle',
            },
            {
                variant: 'secondary',
                intent: 'warning',
                disabled: false,
                isLoading: false,
                class: 'hover:border-negative-strong',
            },
            // Padding
            {
                rounded: false,
                inline: false,
                size: 'large',
                iconOnly: false,
                class: 'px-6',
            },
            {
                rounded: false,
                inline: false,
                size: 'default',
                iconOnly: false,
                class: 'px-4',
            },
            {
                rounded: false,
                inline: false,
                size: 'small',
                iconOnly: false,
                class: 'px-3',
            },
            {
                rounded: false,
                inline: false,
                size: 'tiny',
                iconOnly: false,
                class: 'px-2',
            },
            // Rounded/pill
            {
                rounded: false,
                pill: false,
                size: 'large',
                class: 'rounded-xl',
            },
            {
                rounded: false,
                pill: false,
                size: 'default',
                class: 'rounded-lg',
            },
            {
                rounded: false,
                pill: false,
                size: 'small',
                class: 'rounded-lg',
            },
            {
                rounded: false,
                pill: false,
                size: 'tiny',
                class: 'rounded-md',
            },
        ],
        defaultVariants: {
            variant: 'primary',
            intent: 'neutral',
            size: 'default',
            disabled: false,
            isLoading: false,
            fullWidth: false,
            rounded: false,
            pill: false,
            inline: false,
            iconOnly: false,
        },
    })(props);
}
/**
 * Returns the data-[state=open] class names for a button variant.
 * Used by SelectButton to apply consistent open state styles.
 */
const getButtonOpenStateClassNames = ({ variant = 'primary', intent = 'neutral', inline, }) => {
    switch (variant) {
        case 'primary':
            return 'data-[state=open]:opacity-80';
        case 'secondary':
            if (intent === 'warning') {
                return 'data-[state=open]:bg-negative-strong data-[state=open]:border-negative-strong data-[state=open]:text-inverse';
            }
            return 'data-[state=open]:bg-subtle data-[state=open]:border-base';
        case 'tonal':
            return 'data-[state=open]:bg-subtle';
        case 'text':
            return inline
                ? 'data-[state=open]:text-primary'
                : 'data-[state=open]:text-primary data-[state=open]:bg-soft';
        default:
            assertUnreachableValue(variant);
    }
};
//# sourceMappingURL=classes.js.map